// src/templates.ts
var CONTROL_COMPONENT_TEMPLATE = `{
  template: __TEMPLATE__,
  props: ['Aeppic', 'params', 'control', 'document', 'fieldName', 'fieldInfo', 'form', 'label', 'readonly', 'uniqueId', 'validate'],
  beforeCreate() {
    this.nextValue = null
    this.timerNextValue = null

    this.processNextValue = (value) => {
      this.Aeppic.clearTimeout(this.timerNextValue)
      this.timerNextValue = null

      // delay again if value has changed
      if (value !== this.nextValue) {
        this.setFieldValueAfterDelay(this.nextValue)
        return
      }

      this.setFieldValue(this.nextValue)
      this.nextValue = null
    }

    this.setFieldValueAfterDelay = (value, delay = 500) => {
      this.nextValue = value

      if (this.timerNextValue) {
        return
      }

      // delay processing enough to allow for multiple changes to be batched
      // setTimeout is used to yield as quickly as possible back to the event loop (= UI thread)
      this.timerNextValue = this.Aeppic.setTimeout(() => {
        this.processNextValue(value)
      }, delay)
    }

    this.flushPendingFieldValue = () => {
      if (!this.timerNextValue) {
        return
      }

      const value = this.nextValue
      this.Aeppic.clearTimeout(this.timerNextValue)
      this.timerNextValue = null
      this.nextValue = null
      this.setFieldValue(value)
    }

    function getType(value) {
      return Object.prototype.toString.call(value).slice(8, -1).toLowerCase()
    }

    this.areFieldValuesEqual = (a, b) => {
      if (a === b) {
        return true
      }

      try {
        return JSON.stringify(a) === JSON.stringify(b)
      } catch (error) {
        return false
      }
    }

    this.getFieldValue = () => {
      if (this.isStandalone) {
        return null 
      } else {
        const value = this.document.data[this.fieldName]

        if (value === undefined) {
          console.error('Field is not defined', this.fieldName, this.document)
        }

        // make shallow copy of the array to avoid mutation of original field array before calling setField
        if (Array.isArray(value)) {
          return value.slice()
        }

        return value
      }
    }

    this.setFieldValue = (newValue) => {
      if (this.timerNextValue) {
        this.Aeppic.clearTimeout(this.timerNextValue)
        this.timerNextValue = null
        this.nextValue = null
      }

      if (this.readonly) {
        return
      }

      if (!this.document || !this.fieldName) {
        return
      }

      if (this.document.data[this.fieldName] === newValue) {
        return
      }

      // Default text controls bind with v-model="field". jsdom can fire input
      // during mount before fieldInfo is ready; reading .type then crashes the
      // ava worker (TypeError: Cannot read properties of null (reading 'type')).
      if (!this.fieldInfo || !this.fieldInfo.type) {
        this.document.data[this.fieldName] = newValue
        this.$emit('field-updated', { fieldName: this.fieldName, value: newValue })
        return
      }

      const expectedType = this.fieldInfo.type
      const error = {field:'', type: '', expectedType: expectedType}
      if (this.fieldInfo.cardinality) {
        if (getType(newValue) !== 'array') {
          error.type = getType(newValue)
          error.field = 'value',
          error.expectedType = 'array'
        } else {
          for (let i = 0; i < newValue.length; i += 1) {
            const type = getType(newValue[i])
            
            if (type === expectedType || type === getType(this.fieldInfo.defaultValue)) {
              continue
            }

            error.field =  \`value[index: \${i}]\`
            error.type = type

            break
          }
        }
      } else {
        const type = getType(newValue)
        if (type !== expectedType && type !== getType(this.fieldInfo.defaultValue)) {
          error.field = 'value'
          error.type = type
        }
      }

      if (error.field) {
        console.error(\`Could not set field: Expected "\${error.field}" to be of type "\${error.expectedType}" but got "\${error.type}"\`)
        return
      }

      this.document.data[this.fieldName] = newValue

      this.$emit('field-updated', { fieldName: this.fieldName, value: newValue })
    }

    this.setSubFieldValue = (subField, newValue) => {
      if (this.readonly) {
        console.log('setSubFieldValue: readonly, skipping')
        return
      }

      const current = this.document.data[this.fieldName]
      console.log('setSubFieldValue:', this.fieldName, '.', subField, '=', newValue, 'current:', typeof current)

      if (current !== null && typeof current === 'object' && !Array.isArray(current)) {
        this.document.data[this.fieldName] = Object.assign({}, current, { [subField]: newValue })
        this.$emit('field-updated', { fieldName: this.fieldName, subField, value: newValue })
      }
    }

    this.getValidationErrors = () => {
      // check Aeppic to see if component is not yet fully initialized
      if (!this.validate || !this.Aeppic) {
        return null
      }

      let baseErrors = null

      if (this.Aeppic.isEditableDocument(this.document)) {
        baseErrors = this.document.getValidationErrorsForField(this.fieldName)
      } else {
        // Skip validation for non-editable documents (e.g. list rows).
        // Validation is only meaningful during editing.
        return null
      }

      if (!this.fieldValidationErrors) {
        return baseErrors
      }

      return {
        ...(baseErrors || {}),
        ...this.fieldValidationErrors,
      }
    }

    this.handleFieldChanged = async (newValue, oldValue, context = {}) => {
      if (this.areFieldValuesEqual(oldValue, newValue)) {
        return true
      }

      if (this.controller && typeof this.controller.onFieldChanged === 'function') {
        let callbackResult

        try {
          callbackResult = await this.controller.onFieldChanged(newValue, oldValue, {
            document: context.document || this.document,
            oldDocument: context.oldDocument || this.document,
            fieldName: this.fieldName,
            fieldInfo: this.fieldInfo
          })
        } catch (error) {
          console.warn('Control onFieldChanged callback failed; recreating controller', error)
          callbackResult = false
        }

        if (callbackResult !== false) {
          return true
        }

        return false
      }

      return true
    }

    this.recreateController = () => {
      this._beforeDestroyController()

      const { controller, locals, controllerAeppic, handlers, disposers, sharedContext } = this.createController()

      this.controller = controller
      this.locals = locals
      this.controllerAeppic = controllerAeppic
      this.handlers = handlers
      this.disposers = disposers
      this.sharedContext = sharedContext

      __MOUNT_CONTROLLER__
    }

    this.createController = () => {
      const Aeppic = createControllerAeppic(this.Aeppic, this.document)

      const self = this
      const locals = __NEW_LOCALS__
      const handlers = []

      const sharedContext = Aeppic.SharedContext.forComponent()

      // Stopped when the controller is destroyed or recreated
      const disposers = []

      // Field references in params ([field], [Relation.field]), see field-params.ts
      const fieldParamHost = {
        Aeppic,
        getDocument: () => self.document,
        getParam: (name) => self.params ? self.params[name] : undefined,
        isReadonly: () => self.readonly,
        $watch: (getter, callback, options) => self.$watch(getter, callback, options),
      }

      const controllerVariables = {
        get $el() { return self.$el },
        get $refs() { return self.$refs },
        $listeners: self.$listeners,
        locals,
        Aeppic,
        get Query() { return Aeppic.Query },
        watchParam(...args) { return self.watchParam(...args) },
        fieldParam(name) { return FieldParams.describeFieldParam(self.params ? self.params[name] : undefined) },
        watchFieldParam(name, callback) {
          const stop = FieldParams.watchFieldParam(fieldParamHost, name, callback)
          disposers.push(stop)
          return stop
        },
        setFieldParam(name, value) { return FieldParams.setFieldParam(fieldParamHost, name, value) },
        get readonly() { return self.readonly },
        get DateTime() { return Aeppic.DateTime },
        get Math() { return Aeppic.Math },
        get Anime() { return Aeppic.Anime },
        get control() { return self.control },
        get params() { return self.params },
        get document() { return self.document },
        get form() { return self.form },
        getField() { return self.getFieldValue() },
        setField(value, options) {
          if (options && options.subField) {
            console.log('setField subField:', options.subField, '=', value, 'on', self.fieldName)
            self.setSubFieldValue(options.subField, value)
          } else if (options && options.delay) {
            self.setFieldValueAfterDelay(value)
          } else {
            self.setFieldValue(value)
          }
        },
        get validationErrors() { return self.getValidationErrors() },
        get validate() { return self.validate },
        fieldName: this.fieldName,
        fieldInfo: this.fieldInfo,
        $emit(...args) { return self.$emit(...args) },
        $on(...args) { 
          self.$parent.$on(...args)
          self.$on(...args)
  
          handlers.push(args)
        },
        get SharedContext() {
          return sharedContext
        }
      }
      
      const controller = controllerClass ? new controllerClass(controllerVariables) : null

      return {
        controller,
        locals,
        controllerAeppic: Aeppic,
        handlers,
        disposers,
        sharedContext
      }
    }

    this._beforeDestroyController = () => {
      for (const handlerArgs of this.handlers) {
        this.$off(...handlerArgs) // remove all controller registered listeners (own scope)
        this.$parent.$off(...handlerArgs) // remove all controller registered listeners (parent scope)
      }
  
      this.handlers = []

      for (const dispose of this.disposers || []) {
        dispose()
      }

      this.disposers = []

      this.packages = null
    }
  },
  data() {
    const { controller, locals, controllerAeppic, handlers, disposers, sharedContext } = this.createController()

    return {
      controller,
      locals,
      controllerAeppic,
      packages, // global provided by dynamicComponent
      handlers,
      disposers,
      sharedContext
    } 
  },
  computed: {
    Math() { return this.Aeppic.Math },
    DateTime() { return this.Aeppic.DateTime },
    Anime() { return this.Aeppic.Anime },
    isEditing() {
      return ! this.readonly
    },
    isStandalone() {
      const hasField = this.fieldName && this.form && this.document
      return ! hasField
    },
    field: {
      get() {
        // this.revision
        return this.getFieldValue()
      },
      set(value) {
        this.setFieldValue(value)
      }
    },
    validationErrors() {
      return this.getValidationErrors()
    }
  },
  beforeDestroy() {
    this.flushPendingFieldValue()

    if (this._flushPendingFieldValueOnBlur && this.$el && this.$el.removeEventListener) {
      this.$el.removeEventListener('blur', this._flushPendingFieldValueOnBlur, true)
      this._flushPendingFieldValueOnBlur = null
    }

    __DESTROY_CONTROLLER__

    this._beforeDestroyController()
  },
  mounted() {
    this._flushPendingFieldValueOnBlur = () => {
      this.flushPendingFieldValue()
    }

    if (this.$el && this.$el.addEventListener) {
      this.$el.addEventListener('blur', this._flushPendingFieldValueOnBlur, true)
    }

    __MOUNT_CONTROLLER__
  },
  methods: {
    __METHODS__,
    translate(...args) { 
      return this.Aeppic.translate(...args) 
    },
    watchParam(name, cb, options) {
      if (!name) {
        return
      }

      let lastValue = undefined

      const cbWithPreCheck = (newValue, oldValue) => {
        if (Array.isArray(newValue) || lastValue !== newValue) {
          cb(newValue, oldValue)
        }

        lastValue = newValue
      }
      
      return this.$watch('params.' + name, cbWithPreCheck, options)
    }
  },
  watch: {
    async document(newDoc, oldDoc) {
      const isSameDocumentAndForm = oldDoc && newDoc
          && oldDoc.id === newDoc.id
          && oldDoc.f && newDoc.f
          && oldDoc.f.id === newDoc.f.id
          && oldDoc.f.v === newDoc.f.v

      if (isSameDocumentAndForm) {
        const fieldName = this.fieldName
        const oldValue = oldDoc.data && fieldName ? oldDoc.data[fieldName] : undefined
        const newValue = newDoc.data && fieldName ? newDoc.data[fieldName] : undefined

        if (this.areFieldValuesEqual(oldValue, newValue)) {
          return
        }

        if (await this.handleFieldChanged(newValue, oldValue, { document: newDoc, oldDocument: oldDoc })) {
          return
        }
      }

      this.recreateController()
    },
    field: {
      async handler(newValue, oldValue) {
        if (!await this.handleFieldChanged(newValue, oldValue)) {
          this.recreateController()
        }
      },
      deep: true
    },
  },
  components
}
`;
var LAYOUT_COMPONENT_TEMPLATE = `{
  template: __TEMPLATE__,
  props: ['Aeppic', 'params', 'layout', 'hideComponent', 'showComponent', 'isComponentHidden'],
  data() {
    const Aeppic = createControllerAeppic(this.Aeppic)

    const self = this
    const locals = __NEW_LOCALS__
    const handlers = []

    const sharedContext = Aeppic.SharedContext.forComponent()

    const controllerVariables = {
      get $el() { return self.$el },
      get $refs() { return self.$refs },
      $listeners: self.$listeners,
      locals,
      Aeppic,
      get Query() { return Aeppic.Query },
      get DateTime() { return Aeppic.DateTime },
      get Math() { return Aeppic.Math },
      get Anime() { return Aeppic.Anime },
      get params() { return self.params },
      get layout() { return self.layout },
      translate(...args) { return self.translate(...args)},
      watchParam(...args) { return self.watchParam(...args)},
      $emit(...args) { return self.$emit(...args) },
      $on(...args) { 
        self.$parent.$on(...args)
        self.$on(...args)

        handlers.push(args)
      },
      get SharedContext() {
        return sharedContext
      },
      get EditContext() {
        if (self.editContext) return self.editContext

        return {
          Registry: Aeppic.EditContext.Registry,
          create(options) {
            const handle = Aeppic.EditContext.create(options)
            self.editContext = handle
            return handle
          }
        }
      },
      hideComponent() { return self.hideComponent() },
      showComponent() { return self.showComponent() },
      get isComponentHidden() { return self.isComponentHidden },
    }

    const controller = controllerClass ? new controllerClass(controllerVariables) : null

    return {
      controller,
      locals,
      handlers,
      controllerAeppic: Aeppic,
      sharedContext,
      editContext: null
    }
  },
  beforeDestroy() {
    __DESTROY_CONTROLLER__

    for (const handlerArgs of this.handlers) {
      this.$off(...handlerArgs) // remove all controller registered listeners (own scope)
      this.$parent.$off(...handlerArgs) // remove all controller registered listeners (parent scope)
    }

    this.handlers = []
    this.packages = null
  },
  mounted() {
    __MOUNT_CONTROLLER__
  },
  computed: {
    Math() { return this.Aeppic.Math },
    DateTime() { return this.Aeppic.DateTime },
    Anime() { return this.Aeppic.Anime },
  },
  methods: {
    __METHODS__,
    startEditing(refName) {
      if (!this.$refs[refName]) {
        return
      }

      return this.$refs[refName].startEditing()
    },
    cancelEditing(refName) {
      if (!this.$refs[refName]) {
        return
      }

      return this.$refs[refName].cancelEditing()
    },
    saveAndStopEditing(refName) {
      if (!this.$refs[refName]) {
        return
      }

      return this.$refs[refName].saveAndStopEditing()
    },
    save(refName) {
      if (!this.$refs[refName]) {
        return
      }

      return this.$refs[refName].save()
    },
    translate(...args) { 
      return this.Aeppic.translate(...args) 
    },
    watchParam(name, cb, options) {
      if (!name) {
        return
      }

      let lastValue = undefined

      const cbWithPreCheck = (newValue, oldValue) => {
        if (Array.isArray(newValue) || lastValue !== newValue) {
          cb(newValue, oldValue)
        }

        lastValue = newValue
      }
      
      return this.$watch('params.' + name, cbWithPreCheck, options)
    }
  },
  components
}`;
var DESIGN_COMPONENT_TEMPLATE = `{
  props: ['Aeppic', 'linkedDocument', 'documentEditVersion', 'design', 'edit', 'params', 'hideComponent', 'showComponent', 'isComponentHidden'],
  template: __TEMPLATE__,
  beforeCreate() {
    const self = this
    
    this.createController = () => {
      self.handlers = []
      
      this._isEditing = () => {
        return this.aeLocallyEditableDocument || Aeppic.isEditableDocument(this.linkedDocument)
        // return !!this.aeLocallyEditableDocument
      }

      this._isEditingExternally = () => {
        return !this.aeLocallyEditableDocument && Aeppic.isEditableDocument(this.linkedDocument)
        // return (this._isEditing() && this.aeLocallyEditableDocument === this.linkedDocument)
      }
      
      const controllerAeppic = createControllerAeppic(this.Aeppic, this.linkedDocument)
      const Aeppic = Object.create(controllerAeppic)
      Aeppic.watch = function (identifier, callback) {
        const watchedDocumentId = typeof identifier === 'string' ? identifier : identifier && identifier.id
        const renderedDocument = self.aeLocallyEditableDocument || self.linkedDocument

        if (watchedDocumentId && renderedDocument && watchedDocumentId === renderedDocument.id) {
          const designId = self.design && self.design.id
          const designName = self.design && self.design.data && self.design.data.name

          controllerAeppic.Warn.once(
            'design-controller:watch-rendered-document',
            designId,
            'Aeppic.watch(document, ...) inside a design controller watches the document this design is already rendering. Implement onDocumentVersionChanged(newDocument, oldDocument) instead.'
              + ' design=' + (designName || '<unnamed>') + ' (' + (designId || '<unknown>') + ')'
              + ' document=' + renderedDocument.id
          )
        }

        return controllerAeppic.watch(identifier, callback)
      }
      const locals = __NEW_LOCALS__
  
      this._getForm = () => {
        Aeppic.Log.warn('DEPRECATED: use getFormForDocument in the controller to get a form')
        return Aeppic.getDocumentForm(this.aeLocallyEditableDocument || this.linkedDocument)
      }
      
      const sharedContext = Aeppic.SharedContext.forComponent()

      const controllerVariables = {
        Aeppic,
        get Query() { return Aeppic.Query },
        get DateTime() { return Aeppic.DateTime },
        get Math() { return Aeppic.Math },
        get Anime() { return Aeppic.Anime },
        translate(...args) { return self.translate(...args) },
        get params() { return self.params },
        get document() { return self.aeLocallyEditableDocument || self.linkedDocument },
        get data() { 
          const document = self.aeLocallyEditableDocument || self.linkedDocument
          
          if (!document) {
            return null
          }
          
          return document.data
        },
        get design() { return self.design },
        
        get locals() { return locals },
        
        watchParam(...args) { return self.watchParam(...args) },
        get isEditing() { return self._isEditing() },
        get isEditingExternally() { return self._isEditingExternally() },

        $emit(...args) { return self.$emit(...args) },
        $on(...args) { 
          self.$parent.$on(...args)
          self.$on(...args)
          
          self.handlers.push(args)
        },
        get SharedContext() {
          return sharedContext
        },
        get EditContext() {
          if (self.editContext) return self.editContext

          return {
            Registry: Aeppic.EditContext.Registry,
            create(options) {
              const handle = Aeppic.EditContext.create(options)
              self.editContext = handle
              // Controllers may create a context after an async init() has
              // already allowed the dynamic component to mount.
              if (self.__aeDynamicControllerMounted && self.$el && handle.mount) {
                handle.mount(self.$el)
              }
              return handle
            }
          }
        },
        get $el() { return self.$el },
        get $refs() { return self.$refs },
        $listeners: self.$listeners,

        /* Edit methods */
        startEditing() { return self.startEditing() },
        save() { return self.save() },
        saveAndStopEditing() { return self.saveAndStopEditing() },
        cancelEditing() { return self.cancelEditing() },
        resetEdits() { return self.resetEdits() },
        hideComponent() { return self.hideComponent() },
        showComponent() { return self.showComponent() },
        get isComponentHidden() { return self.isComponentHidden },
      }
  
      const controller = controllerClass ? new controllerClass(controllerVariables) : null

      return { controller, locals, controllerAeppic, handlers: self.handlers, sharedContext }
    }

    this._beforeDestroyController = () => {
      __DESTROY_CONTROLLER__

      for (const handlerArgs of this.handlers) {
        this.$off(...handlerArgs) // remove all controller registered listeners (own scope)
        this.$parent.$off(...handlerArgs) // remove all controller registered listeners (parent scope)
      }

      this.handlers = []

      this.packages = null
    }
  },
  data() {
    const { controller, locals, controllerAeppic, sharedContext } = this.createController()

    return {
      controller,
      locals,
      controllerAeppic,
      packages,
      sharedContext,
      editContext: null,
      aeLocallyEditableDocument: null,
    }
  },
  computed: {
    Math() { return this.Aeppic.Math },
    DateTime() { return this.Aeppic.DateTime },
    Anime() { return this.Aeppic.Anime },
    isEditing() { return this._isEditing() },
    isEditingExternally() { return this._isEditingExternally() },
    document() {
      return this.aeLocallyEditableDocument || this.linkedDocument

      // if (this.isEditing) {
      //   return this.aeLocallyEditableDocument
      // } else {
      //   return this.linkedDocument
      // }
    },
    data() {
      return this.document.data
    },
    form() {
      return this._getForm()
    }
  },
  beforeDestroy() {
    this._beforeDestroyController()
  },
  mounted() {
    __MOUNT_CONTROLLER__
  },
  methods: {
    __METHODS__,
    startEditing() {
      if (this.isEditingExternally) {
        return
      } 
      
      this.aeLocallyEditableDocument = null
      
      // NOTE: Don't use await here since this code is not further compiled down
      // yet and should be es5 compatible
      this.Aeppic.edit(this.linkedDocument.id).then((editable) => {
        this.aeLocallyEditableDocument = editable
      })
    },
    save() {
      if (this.isEditing) {
        if (this.isEditingExternally) {
          this.$emit('save', this.linkedDocument)
        } else {
          this.Aeppic.save(this.aeLocallyEditableDocument)
        }
      }
    },
    saveAndStopEditing() {
      this.save()
      this.cancelEditing()
    },
    cancelEditing() {
      if (this.isEditingExternally) {
        this.$emit('cancel-editing', this.linkedDocument)
      } else {
        this.aeLocallyEditableDocument = null
      }
    },
    resetEdits() {
      if (this.isEditingExternally) {
        this.$emit('reset-edits', this.linkedDocument)
      } else {
        this.startEditing()
      }
    },
    translate(...args) { 
      return this.Aeppic.translate(...args) 
    },
    watchParam(name, cb, options) {
      if (!name) {
        return
      }

      let lastValue = undefined

      const cbWithPreCheck = (newValue, oldValue) => {
        if (Array.isArray(newValue) || lastValue !== newValue) {
          cb(newValue, oldValue)
        }

        lastValue = newValue
      }
      
      return this.$watch('params.' + name, cbWithPreCheck, options)
    },
    async refreshDocumentController(newDoc, oldDoc, options) {
      options = options || {}

      const isSameDocumentAndForm = oldDoc && newDoc
          && oldDoc.id === newDoc.id
          && oldDoc.f && newDoc.f
          && oldDoc.f.id === newDoc.f.id

      const isSameModelVersion = isSameDocumentAndForm && oldDoc.v === newDoc.v
      const shouldCallVersionCallback = this.controller
          && typeof this.controller.onDocumentVersionChanged === 'function'
          && isSameDocumentAndForm
          && (options.force === true || !isSameModelVersion || oldDoc !== newDoc)

      // A documentEditVersion change means the rendered document changed from
      // the client's perspective even when the model version is still stable.
      // Wrapper designs can keep resolved-document state in locals, so they
      // need the same callback used for normal model version changes.
      if (shouldCallVersionCallback) {
        let callbackResult

        try {
          callbackResult = await this.controller.onDocumentVersionChanged(newDoc, oldDoc)
        } catch (error) {
          console.warn('Design onDocumentVersionChanged callback failed; recreating controller', error)
          callbackResult = false
        }

        if (callbackResult !== false) {
          this.$forceUpdate()
          return
        }
      }

      if (isSameModelVersion) {
        this.$forceUpdate()
        return
      }

      this._beforeDestroyController()

      const { controller, locals, controllerAeppic, sharedContext } = this.createController()
      this.controller = controller
      this.locals = locals
      this.controllerAeppic = controllerAeppic
      this.sharedContext = sharedContext

      __MOUNT_CONTROLLER__
    }
  },
  watch: {
    edit: {
      handler() {
        if (this.edit) {
          this.startEditing()
        } else {
          this.cancelEditing()
        }
      },
      immediate: true
    },
    async documentEditVersion(newVersion, oldVersion) {
      if (newVersion === oldVersion) {
        return
      }

      await this.refreshDocumentController(this.linkedDocument, this.linkedDocument, { force: true })
    },
    async linkedDocument(newDoc, oldDoc) {
      if (this.isEditingExternally) {
        return
      } else if (this.isEditing) {
        if (this.aeLocallyEditableDocument && (this.linkedDocument.id === this.aeLocallyEditableDocument.id)) {
          this.Aeppic.integrate(this.aeLocallyEditableDocument, this.linkedDocument)
        } else {
          this.cancelEditing()
          this.startEditing()
        }
      }

      await this.refreshDocumentController(newDoc, oldDoc)
    }
  },
  components
}`;
var LIST_COMPONENT_TEMPLATE = `{
  template: __TEMPLATE__,
  inject: ['getAeppicContext'],
  props: ['Aeppic', 'params', 'list', 'documents', 'rootDocument', 'queryDocument', 'searchOptions', 'filter', 'suspended', 'suspendList', 'unsuspendList', 'isLoading'],
  methods: {
    __METHODS__,
    translate(...args) {
      return this.Aeppic.translate(...args)
    },
    watchParam(name, cb, options) {
      if (!name) {
        return
      }

      let lastValue = undefined

      const cbWithPreCheck = (newValue, oldValue) => {
        if (Array.isArray(newValue) || lastValue !== newValue) {
          cb(newValue, oldValue)
        }

        lastValue = newValue
      }
      
      return this.$watch('params.' + name, cbWithPreCheck, options)
    }
  },
  beforeCreate() {
    this.setSearchSize = (newValue) => {
      this.$parent.manualSearchSize = newValue
    }

    this.setSearchSort = (newValue) => {
      this.$parent.manualSearchSort = newValue
    }

    this.setSearchOptions = (newValue) => {
      this.$parent.manualSearchOptions = newValue
    }

    this.setFilter = (newValue) => {
      this.$parent.manualFilter = newValue
    }
  },
  data() {
    const Aeppic = createControllerAeppic(this.Aeppic, this.rootDocument)

    const self = this
    const locals = __NEW_LOCALS__
    const handlers = []

    const sharedContext = Aeppic.SharedContext.forComponent()

    const controllerVariables = {
      locals,
      Aeppic,
      get Query() { return Aeppic.Query },
      get DateTime() { return Aeppic.DateTime },
      get Math() { return Aeppic.Math },
      get Anime() { return Aeppic.Anime },
      get params() { return self.params },
      get documents() { return self.documents },
      get list() { return self.list },
      get rootDocument() { return self.rootDocument },
      get queryDocument() { return self.queryDocument },
      get searchOptions() { return self.searchOptions },
      get filter() { return self.filter },
      get $el() { return self.$el },
      get $refs() { return self.$refs },
      $listeners: self.$listeners,
      $emit(...args) { return self.$emit(...args) },
      $on(...args) { 
        self.$parent.$on(...args)
        self.$on(...args)

        handlers.push(args)
      },
      get SharedContext() {
        return sharedContext
      },
      watchParam(...args) { return self.watchParam(...args) },
      setSize(value) { self.setSearchSize(value) },
      setSorting(value) { self.setSearchSort(value) },
      setSearchOptions(value) { self.setSearchOptions(value) },
      setFilter(value) { self.setFilter(value) },
      suspendList() { self.suspendList() },
      unsuspendList() { self.unsuspendList() },
      get suspended() { return self.suspended },
      get isLoading() { return self.isLoading },
    }

    const controller = controllerClass ? new controllerClass(controllerVariables) : null

    return {
      controller,
      locals,
      packages,
      controllerAeppic: Aeppic,
      handlers,
      sharedContext
    }
  },
  computed: {
    Math() { return this.Aeppic.Math },
    DateTime() { return this.Aeppic.DateTime },
    Anime() { return this.Aeppic.Anime },
  },
  beforeDestroy() {
    __DESTROY_CONTROLLER__

    for (const handlerArgs of this.handlers) {
      this.$off(...handlerArgs) // remove all controller registered listeners (own scope)
      this.$parent.$off(...handlerArgs) // remove all controller registered listeners (parent scope)
    }

    this.handlers = []
    this.packages = null
  },
  mounted() {
    __MOUNT_CONTROLLER__
  },
  components
}`;
var MOUNT_CONTROLLER_TEMPLATE = `
function isAeDynamicControllerRoot(element) {
  return element &&
    element.getAttribute &&
    element.getAttribute('data-ae-dynamic-controller-root') === 'true'
}

function findUnresolvedDynamicControllerParent(element) {
  var current = element && element.parentElement
  var steps = 0

  while (current && steps < 80) {
    if (isAeDynamicControllerRoot(current) && current.__aeDynamicControllerMounted !== true) {
      return current
    }

    current = current.parentElement
    steps++
  }

  return null
}

function clearDynamicControllerMountWaiter(vm) {
  if (vm && vm.__aeDynamicControllerMountWaiter) {
    vm.__aeDynamicControllerMountWaiter()
    vm.__aeDynamicControllerMountWaiter = null
  }
}

function clearDynamicControllerMountRetry(vm) {
  if (vm && vm.__aeDynamicControllerMountRetryTimer) {
    clearTimeout(vm.__aeDynamicControllerMountRetryTimer)
    vm.__aeDynamicControllerMountRetryTimer = null
  }
}

function scheduleDynamicControllerMountRetry(vm) {
  if (!vm || vm.__aeDynamicControllerMountRetryTimer || vm.__aeDynamicControllerMounted === true) {
    return
  }

  var retryCount = vm.__aeDynamicControllerMountRetryCount || 0
  var delay = retryCount < 10 ? 0 : Math.min(100, retryCount)

  vm.__aeDynamicControllerMountRetryCount = retryCount + 1
  vm.__aeDynamicControllerMountRetryTimer = setTimeout(function () {
    vm.__aeDynamicControllerMountRetryTimer = null

    if (!vm || vm._isBeingDestroyed || vm._isDestroyed || vm.__aeDynamicControllerMounted === true) {
      return
    }

    runDynamicControllerMounted(vm)

    if (vm.__aeDynamicControllerMountWaiter && vm.__aeDynamicControllerMounted !== true) {
      scheduleDynamicControllerMountRetry(vm)
    }
  }, delay)
}

function runDynamicControllerMounted(vm) {
  if (!vm || !vm.$el || vm._isBeingDestroyed || vm._isDestroyed) {
    return
  }

  if (vm.__aeDynamicControllerMounted === true) {
    return
  }

  var unresolvedParent = findUnresolvedDynamicControllerParent(vm.$el)

  if (unresolvedParent) {
    if (vm.__aeDynamicControllerMountWaiter) {
      return
    }

    var stopWaiting = vm.Aeppic.SharedContext.onDynamicComponentMounted(function () {
      clearDynamicControllerMountWaiter(vm)
      vm.$nextTick(function () {
        runDynamicControllerMounted(vm)
      })
    })

    vm.__aeDynamicControllerMountWaiter = stopWaiting
    scheduleDynamicControllerMountRetry(vm)
    return
  }

  clearDynamicControllerMountWaiter(vm)
  clearDynamicControllerMountRetry(vm)
  vm.__aeDynamicControllerMountRetryCount = 0

  if (vm.Aeppic && vm.Aeppic.setContextRootElement) {
    vm.Aeppic.setContextRootElement(vm.$el);
  }

  if (vm.controllerAeppic && vm.controllerAeppic !== vm.Aeppic && vm.controllerAeppic.setContextRootElement) {
    vm.controllerAeppic.setContextRootElement(vm.$el);
  }

  if (vm.sharedContext && vm.sharedContext.mount) {
    vm.sharedContext.mount(vm.$el);
  }

  if (vm.editContext && vm.editContext.mount) {
    vm.editContext.mount(vm.$el);
  }

  if (vm.controller && vm.controller.mounted) {
    try {
      vm.controller.mounted()
    } catch (error) {
      console.error('Error mounting controller', error)
    }
  }

  vm.__aeDynamicControllerMounted = true
  vm.$el.__aeDynamicControllerMounted = true

  if (vm.$el.setAttribute) {
    vm.$el.setAttribute('data-ae-dynamic-controller-mounted', 'true')
  }

  vm.Aeppic.SharedContext.markDynamicComponentMounted(vm.$el)
}

try {
  runDynamicControllerMounted(this)
} catch (error) {
  console.error('Error mounting controller', error)
}`;
var DESTROY_CONTROLLER_TEMPLATE = `if (this.$el) {
  this.__aeDynamicControllerMounted = false
  this.$el.__aeDynamicControllerMounted = false

  if (this.$el.removeAttribute) {
    this.$el.removeAttribute('data-ae-dynamic-controller-mounted')
  }
}

if (this.__aeDynamicControllerMountWaiter) {
  this.__aeDynamicControllerMountWaiter()
  this.__aeDynamicControllerMountWaiter = null
}

if (this.__aeDynamicControllerMountRetryTimer) {
  clearTimeout(this.__aeDynamicControllerMountRetryTimer)
  this.__aeDynamicControllerMountRetryTimer = null
}

if (this.controller && this.controller.destroy) {
  try {
    this.controller.destroy()
  } catch (error) {
    console.error('Error destroying controller', error)
  }
}

this.controller = null
this.locals = null
if (this.controllerAeppic && this.controllerAeppic.release) {
  this.controllerAeppic.release()
}
this.controllerAeppic = null
if (this.sharedContext && this.sharedContext.release) {
  this.sharedContext.release()
}
this.sharedContext = null
if (this.editContext && this.editContext.release) {
  this.editContext.release()
}
this.editContext = null
`;
var DOM_ID_METHOD_TEMPLATE = `id(uniqueNameInsideScope) {
  if (!this.aeDOMIds) {
    this.aeDOMIds = new Map()
  }
  let id = this.aeDOMIds.get(uniqueNameInsideScope)
  if (!id) {
    id = this.Aeppic.newDOMId()
    this.aeDOMIds.set(uniqueNameInsideScope, id)
  }
  return id
}
`;
var COMPONENT_SOURCE_TEMPLATES = {
  layout: LAYOUT_COMPONENT_TEMPLATE,
  design: DESIGN_COMPONENT_TEMPLATE,
  control: CONTROL_COMPONENT_TEMPLATE,
  list: LIST_COMPONENT_TEMPLATE
};

// src/component-source.ts
var CSS_BLOCK_MARKER = /_CSS|_BLOCK/g;
var CSS_BLOCK_MARKER_LEGACY = /_BLOCK/g;
var TEMPLATE_MARKER = /__TEMPLATE__/;
var METHODS_MARKER = /__METHODS__/;
var LOCALS_MARKER = /__NEW_LOCALS__/;
var DESTROY_CONTROLLER_MARKER = /__DESTROY_CONTROLLER__/g;
var MOUNT_CONTROLLER_MARKER = /__MOUNT_CONTROLLER__/g;
function ensureSafeCssClassName(name) {
  return name.replace(/[^-_a-zA-Z0-9]/g, (c) => {
    return c.charCodeAt(0).toString(16);
  });
}
var ComponentSourceGenerator = class {
  constructor(componentDefinition) {
    this.componentDefinition = componentDefinition;
    const blockId = componentDefinition.document.id;
    this.BLOCK = componentDefinition.blockClass ?? ensureSafeCssClassName(`ae-block-${blockId}`);
  }
  /**
   * `templateExpression` replaces the template literal in the component
   * source (the compiler passes `undefined`: it supplies render functions).
   */
  generate({ templateExpression } = {}) {
    let sources = {
      css: this.generateCss(),
      component: `return (${this.generateVueComponentSource(templateExpression)})`,
      controller: this.generateController()
    };
    return sources;
  }
  generateCss() {
    const cssTemplate = this.componentDefinition.document.data.css;
    if (!cssTemplate || cssTemplate === "") {
      return null;
    }
    if (cssTemplate.match(CSS_BLOCK_MARKER_LEGACY)) {
      console.warn("deprecated: CSS still contains legacy `_BLOCK` marker. Please replace with `_CSS`", this.componentDefinition.document);
    }
    const cssPrefix = `/* ${this.componentDefinition.type} : ${this.componentDefinition.document.data.name} (${this.componentDefinition.document.id}@${this.componentDefinition.document.v}) */`;
    return cssPrefix + "\n" + cssTemplate.replace(CSS_BLOCK_MARKER, this.BLOCK);
  }
  generateController() {
    const controller = this.componentDefinition.document.data.controller || "";
    if (controller.match(CSS_BLOCK_MARKER_LEGACY)) {
      console.warn("deprecated: Controller still contains legacy `_BLOCK` marker. Please replace with `_CSS`", this.componentDefinition.document);
    }
    return controller.replace(CSS_BLOCK_MARKER, this.BLOCK);
  }
  /**
   * Generate source but ONLY of the `{...}` object itself, not
   * of the fluff around it like import statements or other
   * required content for creating a full source file.
   */
  /**
   * The full template, as raw text of the template literal the component
   * source holds (the browser sees its cooked value).
   */
  fullTemplate() {
    const definitionDocument = this.componentDefinition.document;
    const type = this.componentDefinition.type;
    const cssClass = `ae-${type}`;
    const innerTemplate = definitionDocument.data.template.replace(CSS_BLOCK_MARKER, this.BLOCK);
    const fullControlTemplate = `<div class="${cssClass} ${this.BLOCK}" data-ae-dynamic-controller-root="true" data-ae-${type}-id="${definitionDocument.id}" data-ae-${type}-v="${definitionDocument.v}">${innerTemplate}</div>`;
    if (innerTemplate.match(CSS_BLOCK_MARKER_LEGACY)) {
      console.warn("deprecated: HTML Template still contains legacy `_BLOCK` marker. Please replace with `_CSS`", this.componentDefinition.document);
    }
    return fullControlTemplate;
  }
  generateVueComponentSource(templateExpression = "`" + this.fullTemplate() + "`") {
    const NEW_LOCALS = `Aeppic.createReactiveObjectProxy({})`;
    const componentSource = this.componentDefinition.componentSourceTemplate.replace(TEMPLATE_MARKER, templateExpression).replace(METHODS_MARKER, DOM_ID_METHOD_TEMPLATE).replace(LOCALS_MARKER, NEW_LOCALS).replace(DESTROY_CONTROLLER_MARKER, DESTROY_CONTROLLER_TEMPLATE).replace(MOUNT_CONTROLLER_MARKER, MOUNT_CONTROLLER_TEMPLATE);
    return componentSource;
  }
};

// src/controller-class.ts
import { enumerateClassMethodsAndProperties } from "@aeppic/shared/class-iterator";
var RENAMED_CONSTRUCTOR = "__constructor__";
var DEFAULT_HIDDEN_GLOBALS = {
  window: void 0,
  document: void 0,
  // setTimeout: undefined,
  // setInterval: undefined,
  chrome: void 0
};
var THIS_GUARD_INSTRUCTION = `/* Aeppic.verify('controller-this', this) */`;
var AWAIT_DETECTOR = /\bawait\b/;
function createControllerClass(codeGroup, name, controllerClassCode, variablesToExpose, globals, runDynamicCode) {
  const rewrittenClassCode = controllerClassSource(codeGroup, name, controllerClassCode, variablesToExpose, globals, runDynamicCode);
  let controllerClass = null;
  if (rewrittenClassCode) {
    controllerClass = runDynamicCode(codeGroup, name, rewrittenClassCode, { ...globals, ...DEFAULT_HIDDEN_GLOBALS });
  }
  return controllerClass;
}
function controllerClassSource(codeGroup, name, controllerClassCode, variablesToExpose, globals, runDynamicCode) {
  const constructorExpression = /\s*constructor\s*\(/;
  const classCodeWithEscapedConstructor = controllerClassCode.replace(constructorExpression, RENAMED_CONSTRUCTOR + "(");
  const classCode = classCodeWithEscapedConstructor.trim();
  let code;
  if (classCode[0] === "c" && classCode.indexOf("class") === 0) {
    code = `${classCode}`;
  } else {
    code = `class ControllerDefinition {
        ${classCode}
      }
    `;
  }
  code = `return ${code}`;
  const controllerDefinitionClass = runDynamicCode(`${codeGroup}/definitions`, name, code, { ...globals, ...DEFAULT_HIDDEN_GLOBALS });
  if (!controllerDefinitionClass) {
    return null;
  }
  const statics = staticMemberNames(controllerDefinitionClass);
  if (statics.length > 0) {
    throw new Error(`The controller class of ${name} has static members (${statics.join(", ")}): controllers don't support static members. Make them instance methods or fields.`);
  }
  return controllerClassSourceFromMembers(definitionMembers(controllerDefinitionClass), variablesToExpose);
}
function staticMemberNames(DefinitionClass) {
  return Reflect.ownKeys(DefinitionClass).filter((key) => {
    if (key === "prototype") {
      return false;
    }
    if (key === "name" || key === "length") {
      const descriptor = Object.getOwnPropertyDescriptor(DefinitionClass, key);
      const builtIn = descriptor && "value" in descriptor && !descriptor.writable && typeof descriptor.value !== "function";
      return !builtIn;
    }
    return true;
  }).map((key) => String(key));
}
function definitionMembers(DefinitionClass) {
  const members = [];
  for (const { name, descriptor } of enumerateClassMethodsAndProperties(DefinitionClass)) {
    members.push({
      name,
      value: definitionFunction(descriptor.value),
      get: definitionFunction(descriptor.get),
      set: definitionFunction(descriptor.set)
    });
  }
  return members;
}
function definitionFunction(fn) {
  if (!fn) {
    return void 0;
  }
  return { source: fn.toString(), isAsync: fn.constructor.name === "AsyncFunction" };
}
function controllerClassSourceFromMembers(members, variablesToExpose) {
  const exposedNames = String(variablesToExpose).split(",").map((name) => name.trim()).filter(Boolean);
  function bindingsFor(body) {
    const dynamicNames = /\beval\b/.test(body) || /\\u(?:[0-9a-fA-F]{4}|\{)/.test(body);
    const names = dynamicNames ? exposedNames : exposedNames.filter((name) => {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return new RegExp("(^|[^a-zA-Z0-9_$])" + escaped + "($|[^a-zA-Z0-9_$])").test(body);
    });
    if (names.length === 0)
      return "";
    return "const { " + names.join(", ") + " } = this.__variables\n";
  }
  const methodsAndProperties = [];
  let constructorCode = "";
  for (const { name, value, get, set } of members) {
    if (name === RENAMED_CONSTRUCTOR) {
      if (!value) {
        throw new Error(`${RENAMED_CONSTRUCTOR} must be the constructor, not an accessor`);
      }
      constructorCode = rewriteConstructor(value);
    } else if (value) {
      methodsAndProperties.push(rewriteMethod(name, value));
    } else if (get || set) {
      if (get) {
        methodsAndProperties.push(rewriteGetter(name, get));
      }
      if (set) {
        methodsAndProperties.push(rewriteSetter(name, set));
      }
    }
  }
  if (constructorCode.match(AWAIT_DETECTOR)) {
    console.warn("You might have used the await keyword in a constructor. `await` cannot be used directly in a constructor. If you want to perform asynchronous work here you can call an `async` function from within the constructor which can use await");
  }
  const code = `  
return class Controller {
  constructor(variables) {
    this.__variables = variables

    ${bindingsFor(constructorCode)}

    try {
      ${constructorCode}
    } catch(error) {
      console.error('Error running Controller constructor() code', error)
    }

    if (this.init && typeof this.init === 'function') {
      try {
        const initialized = this.init()
        if (initialized && typeof initialized.catch === 'function') {
          initialized.catch(error => console.error('Error running Controller init() code', error))
        }
      } catch(error) {
        console.error('Error running Controller init() code', error)
      }
    }
  }

  ${methodsAndProperties.join("\n  ")}
}
`;
  return code;
  function rewriteConstructor(fn) {
    return getFunctionBody(fn.source);
  }
  function rewriteMethod(name, fn) {
    const isAsync = fn.isAsync;
    const methodAsString = fn.source;
    const args = getFunctionArguments(methodAsString);
    const body = getFunctionBody(methodAsString);
    const saferBody = safeBody(body, isAsync);
    return `${isAsync ? "async " : ""}${name} (${args}) {
    ${THIS_GUARD_INSTRUCTION}
    ${bindingsFor(body)}${saferBody}
  }`;
  }
  function safeBody(body, propagateErrors = false) {
    const logAccessOfIdOnUndefined = `if (error?.name === 'TypeError' && error?.message?.includes('read properties of undefined') && error?.message?.includes("'id'")) { console.warn('Attempting to access "id" on undefined variable. Make sure code uses "?." syntax. E.g in formulas {hidden if optionalField?.id }', error.stack); return; }`;
    const logError = `console.error('Error in controller method/getter/setter', error, error.stack)`;
    body = fixTailwindError(body);
    const errorHandler = propagateErrors ? "throw error" : `${logAccessOfIdOnUndefined}
    ${logError}`;
    return `try {
    ${body}
    } catch (error) {
    ${errorHandler}
}`;
  }
  function rewriteGetter(name, fn) {
    const methodAsString = fn.source;
    const body = getFunctionBody(methodAsString);
    const saferBody = safeBody(body);
    return `get ${name}() {
    ${THIS_GUARD_INSTRUCTION}
    ${bindingsFor(body)}
    ${saferBody}
  }`;
  }
  function rewriteSetter(name, fn) {
    const methodAsString = fn.source;
    const args = getFunctionArguments(methodAsString);
    const body = getFunctionBody(methodAsString);
    return `set ${name}(${args}) {
    ${THIS_GUARD_INSTRUCTION}
    ${bindingsFor(body)}${body}
  }
`;
  }
  function getFunctionBody(functionAsString) {
    const bodyStart = findFunctionBodyStart(functionAsString);
    let body = functionAsString.slice(bodyStart + 1, functionAsString.lastIndexOf("}"));
    const reindentedBody = body.replace(/\n  /g, "\n    ");
    return reindentedBody;
  }
  function getFunctionArguments(functionAsString) {
    const args = findFunctionArguments(functionAsString);
    return args;
  }
}
function findFunctionBodyStart(functionAsString) {
  const argsStart = functionAsString.indexOf("(");
  const scanStart = argsStart === -1 ? 0 : findMatchingCloser(functionAsString, argsStart, "(", ")") + 1;
  const bodyStart = findNextCodeCharacter(functionAsString, "{", scanStart);
  if (bodyStart === -1) {
    throw new Error(`Could not find function body in ${functionAsString}`);
  }
  return bodyStart;
}
function findFunctionArguments(functionAsString) {
  const argsStart = functionAsString.indexOf("(");
  if (argsStart === -1) {
    return "";
  }
  const argsEnd = findMatchingCloser(functionAsString, argsStart, "(", ")");
  return functionAsString.slice(argsStart + 1, argsEnd);
}
function findMatchingCloser(source, openIndex, openCharacter, closeCharacter) {
  let depth = 0;
  let stringQuote = null;
  let escaped = false;
  let lineComment = false;
  let blockComment = false;
  for (let index = openIndex; index < source.length; index += 1) {
    const character = source[index];
    const nextCharacter = source[index + 1];
    if (lineComment) {
      if (character === "\n" || character === "\r") {
        lineComment = false;
      }
      continue;
    }
    if (blockComment) {
      if (character === "*" && nextCharacter === "/") {
        blockComment = false;
        index += 1;
      }
      continue;
    }
    if (stringQuote) {
      if (escaped) {
        escaped = false;
      } else if (character === "\\") {
        escaped = true;
      } else if (character === stringQuote) {
        stringQuote = null;
      }
      continue;
    }
    if (character === "/" && nextCharacter === "/") {
      lineComment = true;
      index += 1;
      continue;
    }
    if (character === "/" && nextCharacter === "*") {
      blockComment = true;
      index += 1;
      continue;
    }
    if (character === '"' || character === "'" || character === "`") {
      stringQuote = character;
      continue;
    }
    if (character === openCharacter) {
      depth += 1;
    } else if (character === closeCharacter) {
      depth -= 1;
      if (depth === 0) {
        return index;
      }
    }
  }
  throw new Error(`Could not find matching ${closeCharacter} in ${source}`);
}
function findNextCodeCharacter(source, targetCharacter, startIndex) {
  let stringQuote = null;
  let escaped = false;
  let lineComment = false;
  let blockComment = false;
  for (let index = startIndex; index < source.length; index += 1) {
    const character = source[index];
    const nextCharacter = source[index + 1];
    if (lineComment) {
      if (character === "\n" || character === "\r") {
        lineComment = false;
      }
      continue;
    }
    if (blockComment) {
      if (character === "*" && nextCharacter === "/") {
        blockComment = false;
        index += 1;
      }
      continue;
    }
    if (stringQuote) {
      if (escaped) {
        escaped = false;
      } else if (character === "\\") {
        escaped = true;
      } else if (character === stringQuote) {
        stringQuote = null;
      }
      continue;
    }
    if (character === "/" && nextCharacter === "/") {
      lineComment = true;
      index += 1;
      continue;
    }
    if (character === "/" && nextCharacter === "*") {
      blockComment = true;
      index += 1;
      continue;
    }
    if (character === '"' || character === "'" || character === "`") {
      stringQuote = character;
      continue;
    }
    if (character === targetCharacter) {
      return index;
    }
  }
  return -1;
}
function fixTailwindError(body) {
  const wrongCheck = `typeof tailwind !== 'unknown'`;
  const correctCheck = "(false)";
  return body.replace(wrongCheck, correctCheck);
}

// src/exposed-symbols.ts
var EXPOSED_SYMBOLS = {
  layout: "$el, $refs, $listeners, locals, Aeppic, Query, Math, DateTime, Anime, params, layout, $emit, $on, translate, watchParam, SharedContext, EditContext, hideComponent, showComponent, isComponentHidden",
  design: "locals, Aeppic, Query, Math, DateTime, Anime, params,$el, $refs, $listeners, document, data, design, startEditing, save, saveAndStopEditing, cancelEditing, resetEdits, isEditing, $emit, $on, translate, watchParam, SharedContext, EditContext, hideComponent, showComponent, isComponentHidden",
  control: "$el, $refs, $listeners, Aeppic, Query, Math, DateTime, Anime, locals, control, document, getField, setField, fieldName, fieldInfo, form, params, readonly, watchParam, fieldParam, watchFieldParam, setFieldParam, $on, $emit, translate, validationErrors, validate, SharedContext",
  list: "locals, Aeppic, Query, Math, DateTime, Anime, params, watchParam, $el, $refs, $listeners, list, documents, $emit, $on, rootDocument, setSorting, setSize, setSearchOptions, queryDocument, searchOptions, setFilter, filter, suspended, suspendList, unsuspendList, SharedContext, isLoading"
};

// src/component-build.ts
function componentSources({ type, document, shortBlockClasses = false }) {
  const componentSourceTemplate = COMPONENT_SOURCE_TEMPLATES[type];
  if (!componentSourceTemplate) {
    throw new Error(`Unknown component type ${type}`);
  }
  if (typeof document.data?.template !== "string") {
    throw new Error("template: the document has no template (data.template)");
  }
  const controllerUsesBlock = /_CSS|_BLOCK/.test(document.data?.controller ?? "");
  const blockClass = shortBlockClasses && !controllerUsesBlock ? shortBlockClass(document.id) : void 0;
  const generator = new ComponentSourceGenerator({ type, document, componentSourceTemplate, blockClass });
  const sources = generator.generate({ templateExpression: "undefined" });
  return {
    css: sources.css,
    component: sources.component,
    controller: sources.controller,
    rawTemplate: generator.fullTemplate()
  };
}
function buildComponent(input) {
  const { type, name, document } = input;
  const sources = componentSources(input);
  let template;
  try {
    template = templateLiteralValue(sources.rawTemplate);
  } catch (error) {
    throw new Error(`template: ${describe(error)}`);
  }
  let controller = null;
  if (sources.controller) {
    const globals = { packages: {}, [`${type}Document`]: document };
    try {
      controller = controllerClassSource(`ae-${type}s/controllers`, name, sources.controller, EXPOSED_SYMBOLS[type], globals, run);
    } catch (error) {
      throw new Error(`controller: ${describe(error)}`);
    }
  }
  return componentBuild(sources, template, controller);
}
function buildPreparedComponent({ type, sources, template, controllerMembers }) {
  let controller = null;
  if (sources.controller) {
    if (!controllerMembers) {
      throw new Error("controller: the controller definition was not read (controllerMembers)");
    }
    try {
      controller = controllerClassSourceFromMembers(controllerMembers, EXPOSED_SYMBOLS[type]);
    } catch (error) {
      throw new Error(`controller: ${describe(error)}`);
    }
  }
  return componentBuild(sources, template, controller);
}
function componentBuild(sources, template, controller) {
  return {
    css: sources.css,
    component: sources.component,
    template,
    controller,
    controllerFailed: Boolean(sources.controller) && !controller
  };
}
function templateLiteralValue(raw) {
  let escaped = false;
  for (let i = 0; i < raw.length; i += 1) {
    const character = raw[i];
    if (escaped) {
      escaped = false;
    } else if (character === "\\") {
      escaped = true;
    } else if (character === "`" || character === "$" && raw[i + 1] === "{") {
      throw new Error("Template contains a template literal substitution or backtick");
    }
  }
  if (/\$[$&'`<0-9]/.test(raw)) {
    throw new Error("Template contains a replacement pattern ($&, $', $`, $$, $n)");
  }
  return new Function("return `" + raw + "`")();
}
function shortBlockClass(id) {
  let h1 = 3735928559;
  let h2 = 1103547991;
  for (let i = 0; i < id.length; i += 1) {
    const code = id.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return "ae-b" + (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
function describe(error) {
  if (error instanceof Error) {
    return error.name && error.name !== "Error" ? `${error.name}: ${error.message}` : error.message;
  }
  return String(error);
}
function run(_codePath, _name, code, globals = {}) {
  const names = Object.getOwnPropertyNames(globals);
  return new Function(...names, code)(...names.map((n) => globals[n]));
}

// src/groups.ts
var GROUPS_PATH = "/api/view-components/groups";
function componentGroup(document, rules) {
  const ancestors = Array.isArray(document?.a) ? document.a : [];
  const forms = Array.isArray(document?.a_forms) ? document.a_forms : [];
  for (const rule of rules) {
    if (rule.ancestorForm != null) {
      const wanted = Array.isArray(rule.ancestorForm) ? rule.ancestorForm : [rule.ancestorForm];
      for (let i = ancestors.length - 1; i >= 0; i -= 1) {
        if (wanted.includes(formId(forms[i]))) {
          return { id: `${rule.name}:${ancestors[i]}`, name: rule.name, root: ancestors[i] };
        }
      }
    } else if (rule.childOf != null) {
      const index = ancestors.indexOf(rule.childOf);
      const child = index >= 0 ? ancestors[index + 1] : void 0;
      if (child) {
        return { id: `${rule.name}:${child}`, name: rule.name, root: child };
      }
    } else if (rule.ancestor != null) {
      if (ancestors.includes(rule.ancestor)) {
        return { id: rule.name, name: rule.name, root: rule.ancestor };
      }
    } else {
      return { id: rule.name, name: rule.name, root: "root" };
    }
  }
  return null;
}
function groupRoot(groupId, rules) {
  const separator = groupId.indexOf(":");
  const name = separator >= 0 ? groupId.slice(0, separator) : groupId;
  const rule = rules.find((r) => r.name === name);
  if (!rule) {
    return null;
  }
  if (rule.ancestorForm != null || rule.childOf != null) {
    return separator >= 0 ? groupId.slice(separator + 1) : null;
  }
  return rule.ancestor ?? "root";
}
function groupMembers(documents, groupId, rules) {
  return documents.filter((document) => componentGroup(document, rules)?.id === groupId).map((document) => document.id);
}
function formId(form) {
  return typeof form === "string" ? form.split("@")[0] : "";
}
function groupKey(dts, baseHash, buildKey) {
  return `${dts}.${baseHash}.${buildKey}`;
}
function groupUrls(groupId, key) {
  const base = `${GROUPS_PATH}/${encodeURIComponent(groupId)}@${key}`;
  return { js: `${base}.js`, css: `${base}.css` };
}
function parseGroupFile(file) {
  const match = /^(.+)@(\d+\.[0-9a-f]+\.[0-9a-f]+)\.(js|css|js\.map)$/.exec(file);
  if (!match) {
    return null;
  }
  return { groupId: decodeURIComponent(match[1]), key: match[2], extension: match[3] };
}

// src/module.ts
var CIRCUIT_BREAKER = `function circuitBreakerFor(globals, componentId) {
  const limit = 100000
  const breaker = globals && globals.circuitBreaker

  if (typeof breaker === 'function') {
    return (count, site) => breaker(count, site, componentId)
  }

  return (count, site) => {
    if (count > limit) {
      throw new Error(\`Circuit breaker: \${site} in \${componentId} looped more than \${limit} times in one call\`)
    }
  }
}`;
var PLACEHOLDER_CONTROLLERS = `function placeholderControllers() {
  return placeholderControllers.classes ??= {
    EmptyControllerClass: class EmptyControllerClass {},
    FailedControllerClass: class FailedControllerClass {
      note = 'Placeholder controller due to compile error'
    },
  }
}`;
var SOURCE_PREFIX = "aeppic-components://";
function assembleModule(components, { header = "", sourceMapUrl } = {}) {
  const sorted = [...components].sort((a, b) => a.componentId < b.componentId ? -1 : a.componentId > b.componentId ? 1 : 0);
  const lookup = sorted.map((c, i) => `    ${JSON.stringify(c.componentId)}: register${i},`).join("\n");
  const optionsSources = /* @__PURE__ */ new Map();
  const pieces = [];
  for (const c of sorted) {
    const key = `${c.type}
${c.built.component}`;
    if (!optionsSources.has(key)) {
      const index = optionsSources.size;
      optionsSources.set(key, index);
      pieces.push({ text: optionsFunction(c, index), source: `${SOURCE_PREFIX}options/${c.type}-${index}.js` });
    }
  }
  const css = sorted.filter((c) => !ownStylesheet(c)).map((c) => c.built.css).filter(Boolean).join("\n\n");
  const preamble = `${header}export function ensureComponent(componentId, { ComponentRegistry, globals, packageLoader }) {
  if (ComponentRegistry.has(componentId)) return
  return lookup(componentId)?.(ComponentRegistry, globals, packageLoader)
}

function lookup(componentId) {
  const table = lookup.table ??= {
${lookup}
  }
  return table[componentId]
}

${PLACEHOLDER_CONTROLLERS}

${CIRCUIT_BREAKER}`;
  const all = [
    { text: preamble },
    ...pieces,
    ...sorted.map((c, i) => ({
      text: registerFunction(c, i, optionsSources.get(`${c.type}
${c.built.component}`)),
      source: `${SOURCE_PREFIX}${c.componentId}.js`
    }))
  ];
  let js = all.map((p) => p.text).join("\n\n") + "\n";
  if (sourceMapUrl) {
    js += `//# sourceMappingURL=${sourceMapUrl}
`;
  }
  return { js, css, componentIds: sorted.map((c) => c.componentId), map: sourceMap(all) };
}
function sourceMap(pieces) {
  const sources = [];
  const sourcesContent = [];
  const lines = [];
  let previousSource = 0;
  let previousLine = 0;
  pieces.forEach((piece, index) => {
    if (index > 0) {
      lines.push("");
    }
    const pieceLines = piece.text.split("\n");
    if (!piece.source) {
      for (const _ of pieceLines)
        lines.push("");
      return;
    }
    const sourceIndex = sources.length;
    sources.push(piece.source);
    sourcesContent.push(piece.text);
    pieceLines.forEach((_, line) => {
      lines.push(vlq(0) + vlq(sourceIndex - previousSource) + vlq(line - previousLine) + vlq(0));
      previousSource = sourceIndex;
      previousLine = line;
    });
  });
  return JSON.stringify({ version: 3, sources, sourcesContent, names: [], mappings: lines.join(";") });
}
var BASE64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
function vlq(value) {
  let rest = value < 0 ? -value << 1 | 1 : value << 1;
  let out = "";
  do {
    let digit = rest & 31;
    rest >>>= 5;
    if (rest > 0)
      digit |= 32;
    out += BASE64[digit];
  } while (rest > 0);
  return out;
}
function optionsFunction(c, index) {
  return `function options${index}(components, packages, controllerClass, FieldParams, createControllerAeppic, ${c.type}Document) {
${c.built.component}
}`;
}
function ownStylesheet(c) {
  return c.type === "layout";
}
function registerFunction(c, index, optionsIndex) {
  const documentName = `${c.type}Document`;
  const { built } = c;
  const controllerClass = !built.controller ? `placeholderControllers().${built.controllerFailed ? "FailedControllerClass" : "EmptyControllerClass"}` : `(function (packages, ${documentName}, window, document, chrome) {
${built.controller}
})(packages, ${documentName})`;
  return `function register${index}(ComponentRegistry, globals, packageLoader) {
  ComponentRegistry.register(${JSON.stringify(c.componentId)}, {
    type: ${JSON.stringify(c.type)},
    id: ${JSON.stringify(c.document.id)},
    v: ${JSON.stringify(c.document.v)},
    name: ${JSON.stringify(c.name)},
    packages: ${JSON.stringify(c.document.data.packages ?? [])},
    hasStylesheet: ${built.css ? "true" : "false"},
    stylesheet: ${JSON.stringify(ownStylesheet(c) && built.css || null)},
    async load({ document: ${documentName}, createControllerAeppic }) {
      const { modules: packages, components } = await packageLoader(this.packages)
      const { FieldParams } = globals
      const __circuitBreaker = circuitBreakerFor(globals, ${JSON.stringify(c.componentId)})
      const controllerClass = ${controllerClass}
      const component = options${optionsIndex}(components, packages, controllerClass, FieldParams, createControllerAeppic, ${documentName})
${c.render}
      component.render = render
      component.staticRenderFns = staticRenderFns
      delete component.template
      return component
    },
  })
}`;
}

// src/index.ts
var VERSION = "1.0.16";
export {
  COMPONENT_SOURCE_TEMPLATES,
  CONTROL_COMPONENT_TEMPLATE,
  ComponentSourceGenerator,
  DESIGN_COMPONENT_TEMPLATE,
  DESTROY_CONTROLLER_TEMPLATE,
  DOM_ID_METHOD_TEMPLATE,
  EXPOSED_SYMBOLS,
  GROUPS_PATH,
  LAYOUT_COMPONENT_TEMPLATE,
  LIST_COMPONENT_TEMPLATE,
  MOUNT_CONTROLLER_TEMPLATE,
  VERSION,
  assembleModule,
  buildComponent,
  buildPreparedComponent,
  componentGroup,
  componentSources,
  controllerClassSource,
  controllerClassSourceFromMembers,
  createControllerClass,
  definitionMembers,
  ensureSafeCssClassName,
  groupKey,
  groupMembers,
  groupRoot,
  groupUrls,
  parseGroupFile,
  shortBlockClass,
  staticMemberNames,
  templateLiteralValue
};
